<template>
    <BaseModal :title="'Credential Gating'" @close="closeModal">
        <template #modalContent>
            <div class="modal-content-wrapper">
                <div class="modal-content">
                    <div class="modal-heading">
                        <h3>Verifiable Credential Gating</h3>
                    </div>

                    <div class="status-section">
                        <div v-if="castDetails.vc_gated" class="status-enabled-wrapper">
                            <span class="status-dot"></span>
                            <p>VC Gating is <span class="status-enabled">ENABLED</span></p>
                        </div>
                        <p v-else class="status-description">
                            Enable VC gating to require attendees to present valid Verifiable Credentials before joining.
                        </p>
                    </div>

                    <div class="info-section">
                        <p class="info-title">When enabled, attendees must:</p>
                        <ul class="info-list">
                            <li>Connect their DID wallet</li>
                            <li>Present a valid Verifiable Credential</li>
                            <li>Pass credential verification to join the cast</li>
                        </ul>
                    </div>

                    <div class="warning-section" v-if="!castDetails.vc_gated">
                        <p>//Please note: Enabling VC gating will require all attendees to verify their credentials. Make sure to configure credential requirements after enabling.</p>
                    </div>

                    <div class="action-buttons">
                        <vs-button v-if="!castDetails.vc_gated"
                            class="custom-button"
                            :disabled="isLoading"
                            @click.stop="enableVCGating">
                            {{ isLoading ? '>>processing...' : '>>enable VC gating' }}
                        </vs-button>

                        <template v-else>
                            <vs-button
                                class="custom-button"
                                @click.stop="openCredentialManagement">
                                >>manage credentials
                            </vs-button>
                            <vs-button
                                class="danger-button"
                                :disabled="isLoading"
                                @click.stop="disableVCGating">
                                {{ isLoading ? '>>processing...' : '>>disable' }}
                            </vs-button>
                        </template>
                    </div>
                </div>
                <div class="cast-modal-bottom">
                    <p>>> Cast > Credential Gating</p>
                </div>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import BaseModal from "@/components/common/BaseModal.vue";

export default {
    name: 'CredentialGating',
    props: ['closeModal', 'castDetails', 'setActiveModal', 'updateCastListElement'],
    components: {
        BaseModal,
    },
    data() {
        return {
            isLoading: false,
        };
    },
    methods: {
        async enableVCGating() {
            this.isLoading = true;
            document.getElementById('loading-bg-transparent').style.display = 'flex';
            document.getElementById('loading-bg-transparent-title').innerText = 'Enabling VC Gating...';

            try {
                const formData = new FormData();
                formData.append('vc_gated', 'True');
                formData.append('cast_id', this.castDetails.public_meeting_id);

                await this.$store.dispatch('cast/formSubmit', formData);

                this.$vs.notify({
                    title: 'VC Gating Enabled!',
                    text: 'Your cast now requires Verifiable Credentials for access',
                    color: 'success',
                });

                // Update the cast list to reflect the change
                if (this.updateCastListElement) {
                    this.updateCastListElement({
                        ...this.castDetails,
                        vc_gated: true
                    });
                }

                // Open credential management modal
                this.setActiveModal('credentialManagement');

            } catch (error) {
                console.error('Failed to enable VC gating:', error);
                this.$vs.notify({
                    title: 'Error',
                    text: 'Failed to enable VC gating. Please try again.',
                    color: 'danger',
                });
            } finally {
                this.isLoading = false;
                document.getElementById('loading-bg-transparent').style.display = 'none';
            }
        },

        async disableVCGating() {
            this.isLoading = true;
            document.getElementById('loading-bg-transparent').style.display = 'flex';
            document.getElementById('loading-bg-transparent-title').innerText = 'Disabling VC Gating...';

            try {
                const formData = new FormData();
                formData.append('vc_gated', 'False');
                formData.append('cast_id', this.castDetails.public_meeting_id);

                await this.$store.dispatch('cast/formSubmit', formData);

                this.$vs.notify({
                    title: 'VC Gating Disabled',
                    text: 'Verifiable Credential requirement has been removed',
                    color: 'success',
                });

                // Update the cast list to reflect the change
                if (this.updateCastListElement) {
                    this.updateCastListElement({
                        ...this.castDetails,
                        vc_gated: false
                    });
                }

                this.closeModal();

            } catch (error) {
                console.error('Failed to disable VC gating:', error);
                this.$vs.notify({
                    title: 'Error',
                    text: 'Failed to disable VC gating. Please try again.',
                    color: 'danger',
                });
            } finally {
                this.isLoading = false;
                document.getElementById('loading-bg-transparent').style.display = 'none';
            }
        },

        openCredentialManagement() {
            this.setActiveModal('credentialManagement');
        }
    },
    mounted() {
        document.getElementById('loading-bg-transparent').style.display = 'none';
    }
}
</script>

<style scoped>
.modal-content-wrapper {
    padding: 0 20px 20px 20px;
}

.modal-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.modal-heading h3 {
    color: #FFFFFF;
    font-size: 16px;
    font-weight: 600;
    margin: 0;
}

.status-section {
    padding: 16px;
    background: #1a1a1a;
    border: 1px solid #333;
}

.status-enabled-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
}

.status-dot {
    width: 10px;
    height: 10px;
    background: #22C55E;
    border-radius: 50%;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}

.status-enabled {
    color: #22C55E;
    font-weight: 600;
}

.status-description {
    color: #a6a6a8;
    margin: 0;
    line-height: 1.5;
}

.info-section {
    padding: 16px;
    background: #0a0a0a;
    border: 1px solid #272727;
}

.info-title {
    color: #FFFFFF;
    font-weight: 500;
    margin: 0 0 12px 0;
}

.info-list {
    margin: 0;
    padding-left: 20px;
    color: #a6a6a8;
}

.info-list li {
    margin-bottom: 8px;
    line-height: 1.4;
}

.info-list li:last-child {
    margin-bottom: 0;
}

.warning-section {
    padding: 12px 16px;
    background: rgba(215, 223, 35, 0.1);
    border: 1px solid #D7DF23;
}

.warning-section p {
    color: #D7DF23;
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
}

.action-buttons {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 8px;
}

.custom-button {
    background: #D7DF23 !important;
    color: #000000 !important;
    border: none !important;
    font-weight: 600;
    padding: 10px 20px;
    box-shadow: 3px 3px 0px 0px #545454;
}

.custom-button:hover {
    box-shadow: 3px 3px 0px 0px #D7DF23;
}

.custom-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.danger-button {
    background: transparent !important;
    color: #EF4444 !important;
    border: 1px solid #EF4444 !important;
    font-weight: 600;
    padding: 10px 20px;
}

.danger-button:hover {
    background: rgba(239, 68, 68, 0.1) !important;
}

.danger-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.cast-modal-bottom {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #272727;
}

.cast-modal-bottom p {
    color: #545454;
    margin: 0;
    font-size: 12px;
}
</style>
